import { PostgrestSingleResponse, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { ObservableField } from '../observable-field';
import { ConnectionType } from './match-chronology';

const table = 'broadcast';

export interface BroadcastResponseDto {
  id: number;
  device_id: number;
  tournament_id: number;
  championship_id: number;
  match_id: number;
  template_id: number | null;
  created_at: string;
}

export interface IBroadcast {
  id: number;
  deviceId: number;
  tournamentId: number;
  championshipId: number;
  matchId: number;
  templateId: number | null;
  createdAt: string;
}

export function broadcastMapper(data: BroadcastResponseDto): IBroadcast {
  return {
    id: data.id,
    deviceId: data.device_id,
    tournamentId: data.tournament_id,
    championshipId: data.championship_id,
    matchId: data.match_id,
    templateId: data.template_id,
    createdAt: data.created_at,
  };
}

export default class Broadcast {
  channel?: RealtimeChannel;
  deleteChannel?: RealtimeChannel;
  data: ObservableField<IBroadcast | undefined> = new ObservableField<IBroadcast | undefined>(undefined);
  connectionStatus: ObservableField<ConnectionType> = new ObservableField<ConnectionType>('CLOSED');

  constructor(private supabase: SupabaseClient) {}

  public async fetchByDeviceId(deviceId: number) {
    const { data, error } = (await this.supabase
      .from(table)
      .select()
      .eq('device_id', deviceId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()) as PostgrestSingleResponse<BroadcastResponseDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return broadcastMapper(data);
  }

  public async observeByDeviceId(deviceId: number) {
    this.channel = this.supabase
      .channel(`broadcast_device_${deviceId}`)
      .on(
        // @ts-ignore
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          filter: `device_id=eq.${deviceId}`,
        },
        async (payload) => {
          try {
            this.data.value = await this.fetchByDeviceId(deviceId);
          } catch (e) {
            console.error(e);
            this.data.value = undefined;
          }
        },
      )
      .subscribe((status, err) => {
        console.debug({ status });
        this.connectionStatus.value = status as ConnectionType;
        if (err) {
          console.error({ err });
        }
      });

    // Separate DELETE-only channel without filter — filtered channels don't receive DELETE
    // when the table lacks REPLICA IDENTITY FULL (Supabase default). We match by primary key instead.
    this.deleteChannel = this.supabase
      .channel(`broadcast_delete_${deviceId}`)
      .on(
        // @ts-ignore
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table },
        (payload) => {
          const deletedId = (payload as any).old?.id;
          if (deletedId !== undefined && deletedId === this.data.value?.id) {
            this.data.value = undefined;
          }
        },
      )
      .subscribe((status, err) => {
        console.debug({ status });
        if (err) {
          console.error({ err });
        }
      });
  }

  public async stopObserve() {
    if (this.channel) {
      await this.channel.unsubscribe();
    }
    if (this.deleteChannel) {
      await this.deleteChannel.unsubscribe();
    }
  }
}
